/**
 * @fileoverview Main Application Component - Hi-Tech Textile HR & Payroll System
 * @description Root component with routing and providers. Core pages are eagerly loaded for instant navigation.
 */

import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ViewModeProvider } from "@/contexts/ViewModeContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SessionTimeoutProvider } from "@/components/auth/SessionTimeoutProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loader2 } from 'lucide-react';

// Eager load: only auth page (required for initial load)
import AuthPage from "./pages/Auth";

// Lazy load all other pages for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const EmployeesPage = lazy(() => import("./pages/employees/EmployeesList"));
const AttendanceDaily = lazy(() => import("./pages/attendance/AttendanceDaily"));
const AttendancePeriodsPage = lazy(() => import("./pages/attendance/AttendancePeriodsPage"));
const EmployeeAttendanceHistory = lazy(() => import("./pages/attendance/EmployeeAttendanceHistory"));
const LeaveRequestsPage = lazy(() => import("./pages/leave/LeaveRequests"));
const PayrollPeriodsPage = lazy(() => import("./pages/payroll/PayrollPeriods"));

// Lazy load less-visited pages
const NewEmployeePage = lazy(() => import("./pages/employees/NewEmployee"));
const EditEmployeePage = lazy(() => import("./pages/employees/EditEmployee"));
const AttendanceReports = lazy(() => import("./pages/attendance/AttendanceReports"));
const OvertimePage = lazy(() => import("./pages/attendance/OvertimePage"));
const LeaveCalendarPage = lazy(() => import("./pages/leave/LeaveCalendar"));
const LeaveBalancesPage = lazy(() => import("./pages/leave/LeaveBalances"));
const PayrollDetailsPage = lazy(() => import("./pages/payroll/PayrollDetails"));
const LoansAdvancesPage = lazy(() => import("./pages/payroll/LoansAdvancesPage"));
const IncentivesPage = lazy(() => import("./pages/payroll/IncentivesPage"));
const UserManagementPage = lazy(() => import("./pages/users/UserManagement"));
const DepartmentsPage = lazy(() => import("./pages/master-data/DepartmentsPage"));
const DesignationsPage = lazy(() => import("./pages/master-data/DesignationsPage"));
const NationalitiesPage = lazy(() => import("./pages/master-data/NationalitiesPage"));
const BranchesPage = lazy(() => import("./pages/master-data/BranchesPage"));
const SectionsPage = lazy(() => import("./pages/master-data/SectionsPage"));
const ShiftsPage = lazy(() => import("./pages/master-data/ShiftsPage"));
const HolidaysPage = lazy(() => import("./pages/master-data/HolidaysPage"));
const LeaveTypesPage = lazy(() => import("./pages/master-data/LeaveTypesPage"));
const ProfilePage = lazy(() => import("./pages/profile/ProfilePage"));
const ReportsPage = lazy(() => import("./pages/reports/ReportsPage"));
const WarningsPage = lazy(() => import("./pages/warnings/WarningsPage"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const OvertimeRulesPage = lazy(() => import("./pages/settings/OvertimeRulesPage"));
const CardSettingsPage = lazy(() => import("./pages/settings/CardSettingsPage"));
const EmployeeCardPrint = lazy(() => import("./pages/employees/EmployeeCardPrint"));
const ArchiveViewerPage = lazy(() => import("./pages/settings/ArchiveViewerPage"));
const AuditLogPage = lazy(() => import("./pages/audit/AuditLogPage"));
const ReligionsPage = lazy(() => import("./pages/master-data/ReligionsPage"));
const AccommodationPage = lazy(() => import("./pages/accommodation/AccommodationPage"));
const TransportPage = lazy(() => import("./pages/transport/TransportPage"));
const ProjectDocumentation = lazy(() => import("./pages/documentation/ProjectDocumentation"));
const ExpiringDocumentsPage = lazy(() => import("./pages/hr/ExpiringDocumentsPage"));
const ContractRenewalsPage = lazy(() => import("./pages/hr/ContractRenewalsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

/** Minimal inline loader for lazy pages only */
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <ErrorBoundary fallbackMessage="The application encountered an error">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <ViewModeProvider>
            <SessionTimeoutProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Auth Routes - public */}
                  <Route path="/auth" element={<AuthPage />} />
                  
                  {/* Protected routes - require authentication */}
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/employees" element={<ProtectedRoute><EmployeesPage /></ProtectedRoute>} />
                  <Route path="/attendance" element={<ProtectedRoute permission="can_view_attendance"><AttendanceDaily /></ProtectedRoute>} />
                  <Route path="/attendance/history" element={<ProtectedRoute permission="can_view_attendance"><EmployeeAttendanceHistory /></ProtectedRoute>} />
                  <Route path="/attendance/periods" element={<ProtectedRoute permission="can_view_attendance"><AttendancePeriodsPage /></ProtectedRoute>} />
                  <Route path="/leave" element={<ProtectedRoute permission="can_view_leave"><LeaveRequestsPage /></ProtectedRoute>} />
                  <Route path="/payroll" element={<ProtectedRoute permission="can_view_payroll"><PayrollPeriodsPage /></ProtectedRoute>} />
                  
                  {/* Protected lazy loaded */}
                  <Route path="/employees/new" element={<ProtectedRoute permission="can_add_employee"><NewEmployeePage /></ProtectedRoute>} />
                  <Route path="/employees/:id/edit" element={<ProtectedRoute permission="can_edit_employee"><EditEmployeePage /></ProtectedRoute>} />
                  <Route path="/attendance/reports" element={<ProtectedRoute permission="can_view_reports"><AttendanceReports /></ProtectedRoute>} />
                  <Route path="/overtime" element={<ProtectedRoute permission="can_view_attendance"><OvertimePage /></ProtectedRoute>} />
                  <Route path="/leave/calendar" element={<ProtectedRoute permission="can_view_leave"><LeaveCalendarPage /></ProtectedRoute>} />
                  <Route path="/leave/balances" element={<ProtectedRoute permission="can_view_leave"><LeaveBalancesPage /></ProtectedRoute>} />
                  <Route path="/payroll/:periodId" element={<ProtectedRoute permission="can_view_payroll"><PayrollDetailsPage /></ProtectedRoute>} />
                  <Route path="/payroll/loans-advances" element={<ProtectedRoute permission="can_view_payroll"><LoansAdvancesPage /></ProtectedRoute>} />
                  <Route path="/payroll/incentives" element={<ProtectedRoute permission="can_view_payroll"><IncentivesPage /></ProtectedRoute>} />
                  
                  {/* Admin-only routes */}
                  <Route path="/users" element={<ProtectedRoute permission="can_manage_users"><UserManagementPage /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute roles={['super_admin', 'hr_manager']}><SettingsPage /></ProtectedRoute>} />
                  <Route path="/settings/overtime-rules" element={<ProtectedRoute roles={['super_admin', 'hr_manager']}><OvertimeRulesPage /></ProtectedRoute>} />
                  <Route path="/settings/card-settings" element={<ProtectedRoute roles={['super_admin', 'hr_manager']}><CardSettingsPage /></ProtectedRoute>} />
                  <Route path="/archive" element={<ProtectedRoute roles={['super_admin', 'hr_manager']}><ArchiveViewerPage /></ProtectedRoute>} />
                  <Route path="/audit-logs" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'auditor']}><AuditLogPage /></ProtectedRoute>} />
                  
                  {/* Master data - admin only */}
                  <Route path="/master-data/departments" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><DepartmentsPage /></ProtectedRoute>} />
                  <Route path="/master-data/designations" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><DesignationsPage /></ProtectedRoute>} />
                  <Route path="/master-data/nationalities" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><NationalitiesPage /></ProtectedRoute>} />
                  <Route path="/master-data/branches" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><BranchesPage /></ProtectedRoute>} />
                  <Route path="/master-data/sections" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><SectionsPage /></ProtectedRoute>} />
                  <Route path="/master-data/shifts" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><ShiftsPage /></ProtectedRoute>} />
                  <Route path="/master-data/holidays" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><HolidaysPage /></ProtectedRoute>} />
                  <Route path="/master-data/leave-types" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><LeaveTypesPage /></ProtectedRoute>} />
                  <Route path="/master-data/religions" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><ReligionsPage /></ProtectedRoute>} />
                  
                  {/* Other protected routes */}
                  <Route path="/accommodation" element={<ProtectedRoute><AccommodationPage /></ProtectedRoute>} />
                  <Route path="/transport" element={<ProtectedRoute><TransportPage /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                  <Route path="/reports" element={<ProtectedRoute permission="can_view_reports"><ReportsPage /></ProtectedRoute>} />
                  <Route path="/warnings" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><WarningsPage /></ProtectedRoute>} />
                  <Route path="/employees/print-cards" element={<ProtectedRoute><EmployeeCardPrint /></ProtectedRoute>} />
                  <Route path="/documentation" element={<ProtectedRoute><ProjectDocumentation /></ProtectedRoute>} />
                  <Route path="/expiring-documents" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><ExpiringDocumentsPage /></ProtectedRoute>} />
                  <Route path="/contract-renewals" element={<ProtectedRoute roles={['super_admin', 'hr_manager', 'hr_staff']}><ContractRenewalsPage /></ProtectedRoute>} />
                  
                  {/* Redirects */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </SessionTimeoutProvider>
        </ViewModeProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
